import React from "react";
import { Box, Button, Stack, CircularProgress } from "@mui/material";
import { ArrowBack, ArrowForward, Check } from "@mui/icons-material";

// =============================================================================
// INTERFACE DO COMPONENTE
// =============================================================================

interface StepNavigationButtonsProps {
  activeStep: number;
  totalSteps: number;
  onBack: () => void;
  onNext: () => void;
  onFinish?: () => void;
  isFinishing?: boolean; // Indica se está finalizando o atendimento
}

// =============================================================================
// COMPONENTE PRINCIPAL
// =============================================================================

/**
 * Componente de botões de navegação entre etapas
 * Exibe botões "Voltar" e "Próximo/Finalizar" de forma simples e intuitiva
 *
 * @param activeStep - Índice da etapa ativa (0, 1 ou 2)
 * @param totalSteps - Total de etapas
 * @param onBack - Função chamada ao clicar em "Voltar"
 * @param onNext - Função chamada ao clicar em "Próximo"
 * @param onFinish - Função opcional chamada ao clicar em "Finalizar" na última etapa
 */
export const StepNavigationButtons: React.FC<StepNavigationButtonsProps> = ({
  activeStep,
  totalSteps,
  onBack,
  onNext,
  onFinish,
  isFinishing = false,
}) => {
  // Verifica se está na primeira etapa
  const isFirstStep = activeStep === 0;

  // Verifica se está na última etapa
  const isLastStep = activeStep === totalSteps - 1;

  /**
   * Handler para o botão "Próximo/Finalizar"
   */
  const handleNext = () => {
    // Previne ação durante o loading
    if (isFinishing) {
      return;
    }

    if (isLastStep && onFinish) {
      onFinish();
    } else {
      onNext();
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        borderTop: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
        py: 2,
        px: 3,
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
      >
        {/* Botão Voltar */}
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={onBack}
          disabled={isFirstStep}
          sx={{
            minWidth: 120,
            textTransform: "none",
            fontWeight: 600,
            borderColor: isFirstStep ? undefined : "#00A884",
            color: isFirstStep ? undefined : "#00A884",
            "&:hover": {
              borderColor: isFirstStep ? undefined : "#008c6e",
              bgcolor: isFirstStep ? undefined : "rgba(0, 168, 132, 0.08)",
            },
          }}
        >
          Voltar
        </Button>

        {/* Botão Próximo ou Finalizar */}
        <Button
          variant="contained"
          endIcon={
            isFinishing ? (
              <CircularProgress size={16} color="inherit" />
            ) : isLastStep ? (
              <Check />
            ) : (
              <ArrowForward />
            )
          }
          onClick={handleNext}
          disabled={isFinishing}
          sx={{
            minWidth: 120,
            textTransform: "none",
            fontWeight: 600,
            bgcolor: "#00A884",
            "&:hover": {
              bgcolor: "#008c6e",
            },
            "&:disabled": {
              bgcolor: "#00A884",
              opacity: 0.7,
            },
          }}
        >
          {isFinishing
            ? "Finalizando..."
            : isLastStep
            ? "Finalizar"
            : "Próximo"}
        </Button>
      </Stack>
    </Box>
  );
};

export default StepNavigationButtons;
